import { DateRangeType } from "@fluentui/react-calendar-compat";
import {
  Button,
  FluentProvider,
  Label,
  Select,
  Text,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { CalendarRegular } from "@fluentui/react-icons";
import { useRef } from "react";
import { aimsLightTheme } from "../../../theme/customTheme";
import { dateRangeOptions, maxDate, minDate } from "../utils/dateUtils";
import {
  useStyles,
  datePickerStyles,
  selectStyles,
} from "./DateRangeSelector.styles";

interface DateRangeSelectorProps {
  dateRangeType: string;
  selectedDate: Date;
  customStartDate: Date | undefined;
  customEndDate: Date | undefined;
  customDateError: string | null;
  startDate: Date | undefined;
  endDate: Date | undefined;
  onDateChange: (date: Date, rangeType: string) => void;
  onRangeTypeChange: (rangeType: string) => void;
  onCustomStartDateChange: (date: Date) => void;
  onCustomEndDateChange: (date: Date) => void;
  onCustomDateApply: () => void;
}

export const DateRangeSelector = ({
  dateRangeType,
  selectedDate,
  customStartDate,
  customEndDate,
  customDateError,
  startDate,
  endDate,
  onDateChange,
  onRangeTypeChange,
  onCustomStartDateChange,
  onCustomEndDateChange,
  onCustomDateApply,
}: DateRangeSelectorProps) => {
  const styles = useStyles();
  const calendarPortalRef = useRef<HTMLDivElement>(null);

  return (
    <div className={styles.container}>
      <FluentProvider theme={aimsLightTheme} className={styles.calendarPortal}>
        <div ref={calendarPortalRef} />
      </FluentProvider>

      <div className={styles.sectionContainer}>
        <div className={styles.selectWrapper}>
          <Label
            htmlFor="select-daterange-id"
            className={styles.label}
            required
          >
            Date Range Type
          </Label>
          <Select
            id="select-daterange-id"
            onChange={(ev: any, data: any) => onRangeTypeChange(data.value)}
            value={dateRangeType}
            className={styles.selectField}
            style={selectStyles.root}
            appearance="outline"
          >
            {Object.keys(dateRangeOptions).map((key) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </Select>
        </div>

        <div className={styles.divider} />

        <div className={styles.sectionContainer}>
          <Label className={styles.label}>
            {dateRangeType === "Custom" ? "Custom Date Range" : "Select Date"}
          </Label>

          {dateRangeType !== "Custom" ? (
            <div className={styles.datePickerWrapper}>
              <DatePicker
                placeholder="Select a date..."
                value={selectedDate}
                minDate={minDate}
                maxDate={maxDate}
                className={styles.datePicker}
                appearance="outline"
                mountNode={calendarPortalRef.current}
                contentAfter={<CalendarRegular />}
                calendar={{
                  dateRangeType: dateRangeOptions[
                    dateRangeType as keyof typeof dateRangeOptions
                  ] as DateRangeType,
                  onSelectDate: (date) => {
                    if (date) {
                      onDateChange(date, dateRangeType);
                    }
                  },
                }}
              />
            </div>
          ) : (
            <div>
              <div className={styles.customDateContainer}>
                <div className={styles.datePickerWrapper}>
                  <Label className={styles.label} size="small">
                    Start Date
                  </Label>
                  <DatePicker
                    placeholder="Select start date..."
                    value={customStartDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    className={`${styles.datePicker} ${styles.datePickerField}`}
                    style={datePickerStyles.root}
                    appearance="outline"
                    mountNode={calendarPortalRef.current}
                    contentAfter={<CalendarRegular />}
                    onSelectDate={(date: any) => onCustomStartDateChange(date)}
                  />
                </div>
                <div className={styles.datePickerWrapper}>
                  <Label className={styles.label} size="small">
                    End Date
                  </Label>
                  <DatePicker
                    placeholder="Select end date..."
                    value={customEndDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    className={`${styles.datePicker} ${styles.datePickerField}`}
                    style={datePickerStyles.root}
                    appearance="outline"
                    mountNode={calendarPortalRef.current}
                    contentAfter={<CalendarRegular />}
                    onSelectDate={(date: any) => onCustomEndDateChange(date)}
                  />
                </div>
              </div>
              {customDateError && (
                <Text className={styles.errorText}>{customDateError}</Text>
              )}
              <Button
                onClick={onCustomDateApply}
                appearance="primary"
                className={styles.applyButton}
              >
                Apply Custom Range
              </Button>
            </div>
          )}
        </div>

        {startDate && endDate && (
          <div className={styles.selectedPeriodText}>
            <Text weight="semibold">Selected Period: </Text>
            <Text>
              {startDate.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
              {" - "}
              {endDate.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};
